/**
 * Etiqueta visible del expense en Mark Payment as Paid.
 * No cambia el `name` persistido: solo relabela las opciones del selector.
 * Formato: SHIPPING - TYPE - INV N - AMOUNT - BANK COMMISSION
 */
import { isProformSupplierExpenseType } from "@/utils/supplierExpenseTypes";

function asText(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function extractDynamicListItems(responseData) {
  if (!responseData) return [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData.data)) return responseData.data;
  if (Array.isArray(responseData.items)) return responseData.items;
  return [];
}

export function shippingNameFromExpenseName(name, amount) {
  let text = asText(name);
  text = text.replace(/^expense\s*-\s*/i, "");
  const amountText = asText(amount);
  if (amountText) {
    const trailingAmount = new RegExp(`\\s+${escapeRegExp(amountText)}\\s*$`);
    if (trailingAmount.test(text)) {
      return text.replace(trailingAmount, "").trim();
    }
  }
  return text.replace(/\s+-?\d+(?:[.,]\d+)?\s*$/, "").trim();
}

export function resolveMarkPaidExpenseShippingName(expense, shippingById = {}) {
  if (!expense || typeof expense !== "object") return "";
  const parent = expense.parent;
  if (parent && typeof parent === "object" && parent.name) {
    return asText(parent.name);
  }
  if (expense.shipping && typeof expense.shipping === "object" && expense.shipping.name) {
    return asText(expense.shipping.name);
  }
  if (expense.shippingName) return asText(expense.shippingName);

  const parentId =
    parent && typeof parent === "object" ? parent._id : parent;
  if (parentId && shippingById[String(parentId)]) {
    return asText(shippingById[String(parentId)]);
  }

  const sourceName = expense._markPaidSourceName || expense.name;
  return shippingNameFromExpenseName(sourceName, expense.amount);
}

export function resolveMarkPaidExpenseTypeLabel(expense, typeOptions = []) {
  if (!expense) return "";
  const type = expense.type;
  if (type && typeof type === "object" && type.label) {
    return asText(type.label);
  }
  if (expense.typeLabel) return asText(expense.typeLabel);

  const typeId = type && typeof type === "object" ? type._id : type;
  if (!typeId || !Array.isArray(typeOptions) || typeOptions.length === 0) {
    return "";
  }
  const match = typeOptions.find((opt) => opt && String(opt._id) === String(typeId));
  return match ? asText(match.label) : "";
}

export function formatMarkPaidExpenseLabel(expense, options = {}) {
  if (!expense || typeof expense !== "object") return "";
  const { typeOptions = [], shippingById = {}, bankCommission } = options;

  const shipping = resolveMarkPaidExpenseShippingName(expense, shippingById);
  const type = resolveMarkPaidExpenseTypeLabel(expense, typeOptions);
  const invoice = asText(expense.invoice || expense.invoice_number || "");
  // Mientras el gasto al proveedor es la proforma, su número es el de la proforma (#495).
  const invoicePrefix = isProformSupplierExpenseType(expense.type) ? "PROFORMA N" : "INV N";
  const invoicePart = invoice ? `${invoicePrefix} ${invoice}` : "";
  const amount =
    expense.amount === undefined || expense.amount === null || expense.amount === ""
      ? ""
      : String(expense.amount);
  const commission =
    bankCommission !== undefined && bankCommission !== null && bankCommission !== ""
      ? String(bankCommission)
      : String(expense.bank_commission || 0);

  const parts = [shipping, type, invoicePart, amount, commission].filter(Boolean);
  return parts.join(" - ") || asText(expense.name);
}

export function applyMarkPaidExpenseLabels(expenses, options = {}) {
  if (!Array.isArray(expenses)) return [];
  return expenses.map((expense) => {
    if (!expense || typeof expense !== "object") return expense;
    const sourceName = expense._markPaidSourceName || expense.name;
    return {
      ...expense,
      _markPaidSourceName: sourceName,
      name: formatMarkPaidExpenseLabel(
        { ...expense, name: sourceName },
        options
      ),
    };
  });
}

export function typeofpaymentOptionsFromLists(lists) {
  if (!Array.isArray(lists)) return [];
  const list = lists.find((item) => item && item.type === "typeofpayment");
  return list && Array.isArray(list.values) ? list.values : [];
}

function expenseRefId(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    if (value._id != null) return String(value._id);
    if (value.id != null) return String(value.id);
  }
  return "";
}

function unwrapDynamicDoc(responseData) {
  if (!responseData || typeof responseData !== "object") return null;
  if (
    responseData.data &&
    typeof responseData.data === "object" &&
    !Array.isArray(responseData.data)
  ) {
    return responseData.data;
  }
  if (responseData._id || responseData.name) return responseData;
  return null;
}

export function expenseHasMarkPaidDetails(expense) {
  if (!expense || typeof expense !== "object") return false;
  const type = resolveMarkPaidExpenseTypeLabel(expense);
  const invoice = asText(expense.invoice || expense.invoice_number);
  return Boolean(type || invoice);
}

export function relabelMarkPaidExpenseOption(expense, options = {}) {
  if (!expense || typeof expense !== "object") return expense;
  const sourceName = expense._markPaidSourceName || expense.name;
  return {
    ...expense,
    _markPaidSourceName: sourceName,
    name: formatMarkPaidExpenseLabel({ ...expense, name: sourceName }, options),
  };
}

export async function fetchShippingExpenseForLabel(api, expenseId) {
  if (!api || !expenseId) return null;
  try {
    const response = await api.get(
      `/api/gotrading/dynamic/shipping_expenses/${expenseId}`
    );
    return unwrapDynamicDoc(response && response.data);
  } catch (error) {
    return null;
  }
}

/**
 * Arma la opción visible del expense aplicado. El listado dinámico solo trae
 * 10 filas: si el gasto no está ahí, hay que pedirlo por id (type + invoice).
 */
export async function resolveMarkPaidExpenseOption(
  api,
  expenseRef,
  options = {}
) {
  const id = expenseRefId(expenseRef);
  let expense =
    expenseRef && typeof expenseRef === "object" ? { ...expenseRef } : null;

  if (id && !expenseHasMarkPaidDetails(expense)) {
    const fetched = await fetchShippingExpenseForLabel(api, id);
    if (fetched) {
      expense = { ...(expense || {}), ...fetched };
    }
  }

  if (!expense) return expenseRef || null;
  return relabelMarkPaidExpenseOption(expense, options);
}
